import * as React from "react";
import { MarketplaceReleaseInstance, LegacyReleaseState } from "../../types/release";
import { BasePathContext } from "../../../../reactContexts/basePathContext";
import { useNavigate } from "react-router-dom";
import { Template } from "../../types/template";
import { CPLN_HELM_RELEASE_LEGACY_PREFIX, request, resourceLink } from "../../../../services/cpln";
import { Alert, Button, Modal, notification } from "antd";
import jsYaml from "js-yaml";
import { Loader } from "../../../../components/layout/loader";
import { CodeEditor } from "../../../group/identityMatcher/codeEditor";
import { k8sKeySort } from "../../../../services/utils";
import { getMarketplaceErrorMessage, getTemplateVersion, getVersions } from "../../utils";
import { DOCS_URL } from "../../../../envVariables";
import { MarketplaceIcon } from "../../components/MarketplaceIcon";
import { FilesReducerActionType, useFilesReducer } from "../../template/filesReducer";
import { CodeEditorModal } from "../../../../components/modals/codeEditorModal";
import { ViewModal } from "../../../../components/modals/viewModal";
import { isEqual } from "lodash";
import { CodeSnippet } from "../../../../components/generic/codeSnippet/codeSnippet";
import { FormLabel } from "../../../../components/antd/form/FormLabel";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { addNotification } from "../../../../store/notificationDrawerSlice";
import { selectOrg } from "../../../../store/contextSlice";

interface Props {
  release: MarketplaceReleaseInstance;
  onRefresh?: () => Promise<void>;
}
export const LegacyReleaseUpgrade: React.FC<Props> = ({ release, onRefresh }) => {
  const org = useAppSelector(selectOrg);
  const dispatch = useAppDispatch();

  const basePath = React.useContext(BasePathContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [template, setTemplate] = React.useState<Template>(null as any);
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);
  const [isLoadingUpgrade, setIsLoadingUpgrade] = React.useState(false);

  const [state, setState] = React.useState<LegacyReleaseState>(null as any);
  const versions = getVersions(template);
  const templateVersion = getTemplateVersion(template, release.version);

  const [defaultValues, setDefaultValues] = React.useState("");
  const [initialValues, setInitialValues] = React.useState("");
  const [values, setValues] = React.useState("");

  const [isTemplateView, setIsTemplateView] = React.useState(false);
  const [helmTemplates, setHelmTemplates] = React.useState<any[]>([]);

  const [filesState, filesDispatch] = useFilesReducer();
  const [codeEditorOpenProperty, setCodeEditorOpenProperty] = React.useState("");

  React.useEffect(() => {
    fetchTemplate();
    fetchState();
  }, []);

  async function fetchTemplate() {
    try {
      setIsLoading(true);
      setError("");
      const { data } = await request({ service: "marketplace", url: `/template/${release.template}` });
      setTemplate(data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  async function fetchState() {
    try {
      const { data } = await request({
        url: resourceLink("secret", CPLN_HELM_RELEASE_LEGACY_PREFIX + release.name) + "/-reveal",
      });
      setState(JSON.parse(data.data.payload));
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  React.useEffect(() => {
    if (!state) {
      return;
    }
    if (!template) {
      return;
    }
    if (!versions.includes(release.version)) {
      return;
    }

    const fileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      fileDefaults[property] = value.content;
    }

    filesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: fileDefaults },
    });

    const values = template.versions[release.version].values;
    setDefaultValues(values);
  }, [template, state]);

  React.useEffect(() => {
    if (!defaultValues) {
      return;
    }

    const fileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      fileDefaults[property] = value.content;
    }

    const rev = state.deployments.toSorted((a, b) => {
      if (a.revision > b.revision) return -1;
      if (b.revision > a.revision) return 1;
      return 0;
    })[0];

    const valuesObj: any = jsYaml.load(rev.valuesFiles[0]);
    const valuesObjWithoutFiles: any = jsYaml.load(rev.valuesFiles[0]);

    const fileInitials: { [_: string]: string } = {};

    for (const [property] of Object.entries(template.versions[release.version].files || {})) {
      const fileValueObj = valuesObj[property];
      const fileValueString = jsYaml.dump(fileValueObj);

      let content = fileValueString || fileDefaults[property] || "";

      if (content.trimStart().startsWith("|-")) {
        // remove initial "|- "" and remove indent that was added manually
        content = content
          .slice(3)
          .split("\n")
          .map((l) => l.slice(2))
          .join("\n");
      }

      fileInitials[property] = content;

      filesDispatch({
        type: FilesReducerActionType.SET_FILE,
        payload: { content: content, property: property },
      });
      delete valuesObjWithoutFiles[property];
    }

    filesDispatch({
      type: FilesReducerActionType.SET_INITIALS,
      payload: { content: "", property: "", defaults: fileInitials },
    });

    const valuesObjWithoutFilesString = jsYaml.dump(valuesObjWithoutFiles);

    setInitialValues(valuesObjWithoutFilesString);
    setValues(valuesObjWithoutFilesString);
  }, [defaultValues]);

  function getProcessedValues(): string {
    let processedValues = values;
    if (!templateVersion) {
      return processedValues;
    }
    if (templateVersion.files && Object.keys(templateVersion.files).length > 0) {
      for (let fileProperty of Object.keys(templateVersion.files)) {
        // TODO for handling multi files
        // const fileInfo = templateVersion.chart.files[fileProperty];
        const fileValue = filesState.current[fileProperty] || "";

        const indentCount = 2;
        const indentedFileValue = fileValue
          .split("\n")
          .map((line) => " ".repeat(indentCount) + line)
          .join("\n");

        processedValues += "\n" + fileProperty + ": |- \n" + indentedFileValue;
      }
    }
    return processedValues;
  }

  function handleUpgrade() {
    const gvcDisplay = release.gvc && release.gvc !== " " ? release.gvc : null;
    Modal.confirm({
      title: "Confirm Upgrade",
      content: (
        <div className="flex flex-col gap-2">
          <div>Are you sure you want to upgrade this release?</div>
          {gvcDisplay && (
            <div>
              <span className="font-medium">GVC:</span>{" "}
              <span className="font-bold" style={{ color: "var(--color-primary)" }}>
                {gvcDisplay}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium">Release Name:</span>{" "}
            <span className="font-bold" style={{ color: "var(--color-primary)" }}>
              {release.name}
            </span>
          </div>
          <div>
            <span className="font-medium">Version:</span> <span className="font-bold">{release.version}</span>
          </div>
        </div>
      ),
      okText: "Upgrade",
      okButtonProps: { type: "primary", className: "action" },
      cancelText: "Cancel",
      onOk: async () => {
        await performUpgrade();
      },
    });
  }

  async function performUpgrade() {
    try {
      setIsLoadingUpgrade(true);
      await request({
        service: "marketplace",
        method: "post",
        url: `/helm/install`,
        body: {
          org: release.org,
          gvc: release.gvc,
          name: release.name,
          template: release.template,
          version: release.version,
          values: getProcessedValues(),
          action: "upgrade",
        },
      });
      notification.success({ message: "Success", description: "Upgraded template" });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Upgrade",
        success: true,
        message: `Upgraded legacy release: ${release.name}`,
      }));
      setIsLoadingUpgrade(false);
      if (onRefresh) {
        await onRefresh();
      }
      navigate(basePath.replace("/legacy/", "/"));
    } catch (e) {
      setIsLoadingUpgrade(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({ message: "Failed", description: errorMessage });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Upgrade",
        success: false,
        message: `Failed to upgrade legacy release: ${release.name}`,
        details: Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage,
      }));
    }
  }

  async function handleTemplate() {
    try {
      setIsLoadingTemplate(true);
      const { data } = await request({
        service: "marketplace",
        method: "post",
        url: `/helm/template`,
        body: {
          org: org,
          gvc: release.gvc,
          name: release.name,
          template: template.name,
          version: release.version,
          values: getProcessedValues(),
        },
      });
      setHelmTemplates(jsYaml.loadAll(data.message));
      setIsTemplateView(true);
      notification.success({ message: "Success", description: "Templated" });
      setIsLoadingTemplate(false);
    } catch (e) {
      setIsLoadingTemplate(false);
      setHelmTemplates([]);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({ message: "Failed", description: errorMessage });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Template",
        success: false,
        message: `Failed to template: ${release.name}`,
        details: Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage,
      }));
    }
  }

  if (error) {
    return <Alert type={"error"} message={"Failed to fetch template values"} description={error} />;
  }

  if (!template || isLoading) {
    return (
      <div className="relative">
        <Loader
          style={{
            width: 100,
            height: 100,
            margin: 0,
            left: `calc(50% - 50px)`,
            right: "unset",
            top: 150,
            bottom: "unset",
          }}
        />
      </div>
    );
  }

  const filesAreDirty = !isEqual(filesState.current, filesState.initials);

  return (
    <>
      <div style={{ maxWidth: 900 }}>
        <div className="header-inputs">
          <div
            className="flex mb-4 px-4 py-1 rounded w-min"
            style={{ backgroundColor: "white", color: "var(--color-gray-1600)" }}
          >
            {template.hasIcon !== false && (
              <div style={{ width: 50 }} className="flex items-center">
                <MarketplaceIcon templateName={template.name} hasIcon={template.hasIcon} className="object-fit" />
              </div>
            )}
            <div className="flex flex-col ml-4">
              <span className="leading-none text-lg font-semibold">{template.name}</span>
              <span className="leading-none text-sm">{templateVersion?.category}</span>
            </div>
          </div>
          <div className="flex items-start">
            <div style={{ width: 289, marginRight: 16 }}>
              {/* <NGFormLabel>GVC</NGFormLabel> */}
              <div className="mb-4">{release.gvc}</div>
            </div>
            <div style={{ width: 289, marginRight: 16 }}>
              {/* <NGFormLabel>Release Name</NGFormLabel> */}
              <div className="mb-4">{release.name}</div>
            </div>
            <div style={{ width: 290 }}>
              {/* <NGFormLabel>Template Version</NGFormLabel> */}
              <div className="mb-4">{release.version}</div>
            </div>
          </div>
        </div>
        <FormLabel>Values</FormLabel>
        <div className="text-sm">
          <CodeEditor language="yaml" options={{}} value={values} setValue={setValues} />
        </div>
        {templateVersion ? (
          Object.keys(templateVersion.files || {}).length < 1 ? null : (
            <>
              {Object.entries(templateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                if (fileInfo.type === "multi") {
                  return null;
                }

                const content = filesState.current[fileProperty];

                return (
                  <div className="my-4">
                    {content ? (
                      <div>
                        <FormLabel>{fileInfo.label}</FormLabel>
                        <Button type={"default"} onClick={() => setCodeEditorOpenProperty(fileProperty)}>
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <FormLabel>{fileInfo.label}</FormLabel>
                        <Button className="mt-2" type="primary" onClick={() => setCodeEditorOpenProperty(fileProperty)}>
                          Set Data
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )
        ) : null}
        <div className="mt-4 flex items-center">
          <Button
            style={{ width: 220, marginRight: 10 }}
            disabled={(!filesAreDirty && values === initialValues) || isLoadingUpgrade || isLoadingTemplate}
            type={"default"}
            danger
            onClick={() => {
              filesDispatch({ type: FilesReducerActionType.RESET_TO_INITIALS, payload: { content: "", property: "" } });
              setValues(initialValues);
            }}
          >
            Reset
          </Button>
          <Button
            style={{ width: 220, marginRight: 10 }}
            // TODO we can have a way of validating the values
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingTemplate}
            type={"primary"}
            onClick={() => handleTemplate()}
          >
            Template
          </Button>
          <Button
            style={{ width: 220 }}
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingUpgrade}
            type={"primary"}
            className={"action"}
            onClick={() => handleUpgrade()}
          >
            Upgrade App
          </Button>
        </div>
      </div>
      <div className={`mt-4 p-4 border`}>
        <p>
          Install via terminal using the{" "}
          <a href={`${DOCS_URL}/guides/cpln-helm#install-a-release`} target="_blank" className="color-link">
            Helm Install
          </a>{" "}
          CLI command, after{" "}
          <a href={`${DOCS_URL}/guides/cpln-helm#install-a-release`} target="_blank" className="color-link">
            downloading the template repo
          </a>
          , navigating to the template and updating the <span className="font-bold">values.yaml</span> file.
        </p>
        <CodeSnippet
          code={`cpln helm install ${
            release.name || "{name}"
          } -f values.yaml --tag cpln/marketplace=true --tag cpln/marketplace-gvc=${
            release.gvc || "{gvc}"
          } --tag cpln/marketplace-template=${release.template} --tag cpln/marketplace-template-version=${
            release.version
          } --org ${org} --gvc ${release.gvc || "{gvc}"}`}
          className="mt-3 text-sm"
        />
      </div>
      {!!codeEditorOpenProperty ? (
        <CodeEditorModal
          title={templateVersion ? templateVersion.files![codeEditorOpenProperty].label : "File"}
          initialValue={filesState.current[codeEditorOpenProperty] || ""}
          open={!!codeEditorOpenProperty}
          onOk={(value) => {
            filesDispatch({
              type: FilesReducerActionType.SET_FILE,
              payload: { content: value, property: codeEditorOpenProperty },
            });
            setCodeEditorOpenProperty("");
          }}
          onClose={() => setCodeEditorOpenProperty("")}
        />
      ) : null}
      <ViewModal
        visible={isTemplateView}
        onClose={() => setIsTemplateView(false)}
        closable={!isLoadingUpgrade}
        title={`Template Preview - ${helmTemplates.length} Items`}
        filename={`marketplace-${template.name}-items`}
        rawContent={jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort })}
        rawLanguage="yaml"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsTemplateView(false)} disabled={isLoadingUpgrade}>
              Cancel
            </Button>
            <Button
              type="primary"
              className="action"
              onClick={performUpgrade}
              loading={isLoadingUpgrade}
              disabled={isLoadingUpgrade}
            >
              Upgrade
            </Button>
          </div>
        }
      />
    </>
  );
};
